import type { ReploElementVersionFilter } from "schemas/generated/element";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import {
  selectDraftElementId,
  setElementRevisions,
} from "@v1/reducers/core-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { elementRouter } from "@v2/trpc/routes/element";
import { useOnScreen } from "replo-utils/react/use-on-screen";

/**
 * Hook to manage version history filter state and (optionally) fetch revisions.
 * You must specify shouldFetchRevisions to true or false.
 */
export default function useElementVersioning({
  shouldFetchRevisions,
}: {
  shouldFetchRevisions: boolean;
}) {
  const dispatch = useEditorDispatch();
  const [filterBy, setFilterBy] =
    React.useState<ReploElementVersionFilter>("all");
  const draftElementId = useEditorSelector(selectDraftElementId);

  // Use the lightweight metadata endpoint for the list
  const { data, status, fetchNextPage, isFetchingNextPage, hasNextPage } =
    elementRouter.findRevisionsMetadata.useInfiniteQuery(
      draftElementId
        ? {
            filterBy,
            elementId: draftElementId,
            limit: 25,
          }
        : skipToken,
      {
        initialCursor: 0,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        enabled: shouldFetchRevisions,
      },
    );

  const revisions = React.useMemo(() => {
    return data?.pages.map((group) => group.elementRevisions).flat();
  }, [data]);

  const loadMoreRef = useOnScreen(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  });

  React.useEffect(() => {
    if (revisions && draftElementId) {
      dispatch(
        setElementRevisions({
          [draftElementId]: revisions,
        }),
      );
    }
  }, [revisions, draftElementId, dispatch]);

  return {
    versions: revisions ?? [],
    isLoading: status === "pending",
    isFetchingNextPage,
    loadMoreRef,
    filterBy,
    setFilterBy,
    hasNextPage,
  };
}
